import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, downloadExcel, downloadPdf } from "../lib/api";
import { RESIDENT_STATUS, SERVICES, CARE_ROLES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus, Download, Search, Pencil, Trash2, Users, AlertCircle, CheckCircle2, Phone, ShieldAlert, ArrowRight, Lock,
} from "lucide-react";

const EMPTY = {
  first_name: "", last_name: "", room: "", entry_date: "", status: "actif",
  contact_name: "", contact_phone: "", contact_relation: "", dossier_complete: false,
  missing_documents: [], services: [], notes: "", etage: "", incontinent: false,
};

export default function Residents() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCare = CARE_ROLES.includes(user?.role);
  // Soignants : champs administratifs désactivés. Administratifs : bloc incontinence désactivé.
  const adminFieldsDisabled = isCare;
  const incBlockDisabled = !isCare;
  const stLabel = (s) => t(`enums.residentStatus.${s}`, { defaultValue: RESIDENT_STATUS[s]?.label || s });
  const [list, setList] = useState([]);
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/residents", { params: search ? { search } : {} });
    setList(data);
  };
  useEffect(() => { load(); }, [search]);
  useEffect(() => { api.get("/inc/setup").then(({ data }) => setUnits([...(data.units || [])].sort((a, b) => a.display_order - b.display_order))).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...EMPTY, ...r, missing_documents: r.missing_documents || [], services: r.services || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error(t("residents.nameRequired"));
    const payload = { ...form, missing_documents: (form.missing_documents || []), services: form.services || [] };
    if (editing) await api.put(`/residents/${editing.id}`, payload);
    else await api.post("/residents", payload);
    setOpen(false);
    toast.success(editing ? t("residents.updated") : t("residents.added"));
    load();
  };

  const remove = async (r) => {
    await api.delete(`/residents/${r.id}`);
    toast.success(t("residents.deleted"));
    load();
  };

  const toggleService = (s) => {
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div data-tour="res-header">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400">{t("residents.tag")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl flex items-center justify-center bg-emerald-500/20 text-emerald-400"><Users className="h-6 w-6" /></span> {t("residents.title")}
          </h1>
          <p className="text-muted-foreground mt-1">{t("residents.subtitle")}</p>
        </div>
        <div className="flex gap-2 no-print">
          <Button variant="outline" className="h-11" onClick={() => downloadExcel("residents")} data-testid="export-residents">
            <Download className="h-4 w-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" className="h-11" onClick={() => downloadPdf("residents")} data-testid="export-residents-pdf">
            <Download className="h-4 w-4 mr-2" /> PDF
          </Button>
          <Button className="h-11 bg-emerald-600 hover:bg-emerald-500" onClick={openNew} data-testid="add-resident-btn" data-tour="res-add"><Plus className="h-4 w-4 mr-2" /> {t("residents.addBtn")}</Button>
        </div>
      </div>

      <div className="relative mt-6 max-w-md no-print">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="resident-search" data-tour="res-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("residents.searchPlaceholder")} className="h-11 pl-10" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5 mt-6">
        {list.map((r, i) => {
          const st = RESIDENT_STATUS[r.status] || RESIDENT_STATUS.actif;
          return (
          <motion.div
            key={r.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.3) }}
            className="glass rounded-2xl card-3d p-5"
            data-testid={`resident-card-${r.id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold leading-tight">{r.first_name} {r.last_name}</h3>
                <p className="text-sm text-muted-foreground">{t("residents.room")} {r.room || "—"}</p>
              </div>
              <span className="text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: st.bg, color: st.text }}>{stLabel(r.status)}</span>
            </div>

            <div className="mt-3 flex items-center gap-2">
              {r.dossier_complete ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400"><CheckCircle2 className="h-4 w-4" /> {t("residents.complete")}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-400"><AlertCircle className="h-4 w-4" /> {t("residents.missingDocs", { n: r.missing_documents?.length || 0 })}</span>
              )}
            </div>

            {r.missing_documents?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {r.missing_documents.map((d) => (
                  <span key={d} className="text-[11px] rounded-md px-2 py-0.5 bg-amber-500/15 text-amber-300">{d}</span>
                ))}
              </div>
            )}

            <div className="mt-3 text-sm text-foreground/80 space-y-1">
              {r.contact_name && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /> {r.contact_name} {r.contact_relation && `(${r.contact_relation})`} · {r.contact_phone}</p>}
              {r.services?.length > 0 && <p className="text-xs text-muted-foreground">{t("residents.services")} : {r.services.join(", ")}</p>}
              {r.etage && <p className="text-xs"><span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${r.incontinent ? "bg-rose-500/15 text-rose-300" : "bg-white/5 text-muted-foreground"}`}>{r.incontinent ? t("residents.incontinent") : t("residents.continent")} · {t("residents.floorSheet")}</span></p>}
            </div>

            <div className="flex gap-2 mt-4 no-print">
              <Button variant="outline" size="sm" className="h-9" onClick={() => openEdit(r)} data-testid={`edit-resident-${r.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("common.edit")}</Button>
              <Button variant="ghost" size="sm" className="h-9 text-red-400 hover:bg-red-500/10" onClick={() => remove(r)} data-testid={`del-resident-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </motion.div>
          );
        })}
        {list.length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">{t("residents.none")}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? t("residents.dialogEdit") : t("residents.dialogNew")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{t("residents.dialogDesc")}</DialogDescription>
          <div className="space-y-4 py-2">
            {isCare && (
              <div className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-rose-500/10 p-4" data-testid="care-warning-banner">
                <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-400/20 blur-2xl" />
                <div className="relative flex gap-3">
                  <span className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center bg-amber-400/20 text-amber-300 ring-1 ring-amber-300/30">
                    <ShieldAlert className="h-5 w-5" />
                  </span>
                  <div className="space-y-2">
                    <p className="font-display text-sm font-bold text-amber-200">{t("residents.careWarningTitle")}</p>
                    <p className="text-xs leading-relaxed text-amber-100/80">{t("residents.careWarningText")}</p>
                    <p className="text-[11px] text-amber-100/60">{t("residents.careRestrictNote")}</p>
                    <Button type="button" size="sm" variant="outline" className="h-8 mt-1 border-amber-400/40 text-amber-200 hover:bg-amber-400/10" onClick={() => { setOpen(false); navigate("/incontinence"); }} data-testid="care-goto-units">
                      {t("residents.careWarningGoto")} <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("residents.firstName")}</Label><Input data-testid="r-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div className="space-y-2"><Label>{t("residents.lastName")}</Label><Input data-testid="r-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>{t("residents.room")}</Label>
              <Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} />
            </div>
            <div className={`rounded-xl border p-4 space-y-3 ${incBlockDisabled ? "border-white/5 bg-white/[0.02]" : "border-white/10"}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider font-semibold text-sky-300">{t("residents.otherInfo")}</p>
                {incBlockDisabled && <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> {t("residents.adminIncRestrictNote")}</span>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("residents.floorUnit")}</Label>
                  <Select value={form.etage || "none"} disabled={incBlockDisabled} onValueChange={(v) => setForm({ ...form, etage: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="r-etage"><SelectValue placeholder={t("residents.chooseFloor")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("residents.noneOpt")}</SelectItem>
                      {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("residents.incontinentQ")}</Label>
                  <Select value={form.incontinent ? "oui" : "non"} disabled={incBlockDisabled} onValueChange={(v) => setForm({ ...form, incontinent: v === "oui" })}>
                    <SelectTrigger data-testid="r-incontinent"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="oui">{t("common.yes")}</SelectItem><SelectItem value="non">{t("common.no")}</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {form.etage
                  ? (form.incontinent ? t("residents.hintFloorInc") : t("residents.hintFloorCont"))
                  : t("residents.hintNoFloor")}
              </p>
            </div>
            <div className="space-y-2"><Label>{t("residents.remarks")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} data-testid="resident-save">{editing ? t("common.update") : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
